
import React, { useEffect } from 'react';
import { Loader2, X } from 'lucide-react';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import './LoadingOverlay.css';

// For debugging
const DEBUG = true;

interface LoadingOverlayProps {
  visible: boolean;
  progress: {
    current: number;
    total: number;
  };
  onClose?: () => void;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ visible, progress, onClose }) => {
  // Ensure we have valid progress values
  const safeProgress = {
    current: progress?.current || 0,
    total: progress?.total || 1
  };
  
  const progressPercentage = safeProgress.total > 0 ? Math.round((safeProgress.current / safeProgress.total) * 100) : 0;

  // Debug logging and body scroll control
  useEffect(() => {
    if (DEBUG) {
      console.log('LoadingOverlay visible state changed:', visible);
      console.log('LoadingOverlay progress:', progress);
    }
    
    if (visible) {
      document.body.style.overflow = 'hidden'; // Prevent scrolling when overlay is visible
    } else {
      document.body.style.overflow = ''; // Restore scrolling when overlay is hidden
    }
    
    return () => {
      document.body.style.overflow = ''; // Restore scrolling when component unmounts
    };
  }, [visible, progress]);
  
  // Don't render anything if not visible
  if (!visible) {
    if (DEBUG) console.log('LoadingOverlay not rendering because visible is false');
    return null;
  }
  
  if (DEBUG) {
    console.log('LoadingOverlay rendering with progress:', progress);
    console.log('Progress percentage:', progressPercentage);
  }

  return (
    <div className="loading-overlay">
      <div className="loading-overlay-content relative">
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1 rounded-full hover:bg-muted transition-colors"
            aria-label="Close"
          >
            <X className="h-6 w-6 text-destructive" />
          </button>
        )}
        <div className="loading-overlay-spinner">
          {/* Spinner */}
          <div className="relative">
            <Loader2 className="h-16 w-16 text-sage-600 animate-spin" />
          </div>
          
          {/* Processing text */}
          <div className="text-center space-y-2">
            <p className="text-xl font-semibold text-gray-900">
            Processing {`${safeProgress.current} of ${safeProgress.total}`} images
          </p>
          </div>
          
          {/* Progress bar and percentage */}
          {safeProgress.total > 0 && (
            <div className="w-full space-y-3">
              <div className="flex items-center gap-4">
                <Progress value={progressPercentage} className="h-3 w-full" />
                <div className="inline-flex items-center px-4 py-2 rounded-full text-sm bg-sage-600 text-white flex-shrink-0">
                  <div className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></div>
                  Processing
                </div>
              </div>
              <p className="text-center text-lg font-medium text-gray-700">
                {progressPercentage}% complete
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoadingOverlay;
